"use client";

import type { ActionReveal } from "@/lib/api";
import { surfaceLabel } from "@/lib/stores/chatReveal";

interface Props {
  reveal: ActionReveal;
  /** Fires when the user clicks "Open" — should call request() + closeChat(). */
  onOpen: (reveal: ActionReveal) => void;
}

/**
 * U24 — Inline chat banner shown after an action commit returns a reveal.
 *
 * Replaces the generic "Done!" toast with a clickable "land me on the
 * thing you just created" affordance. Forest-accent styling matches the
 * biophilic palette (no red, gentle confirmation green); the arrow icon
 * + "Open in {Surface}" copy makes the navigation intent obvious.
 */
export default function RevealBanner({ reveal, onOpen }: Props) {
  const surface = surfaceLabel(reveal.surface);
  return (
    <div
      data-testid="chat-reveal-banner"
      className="my-2 flex items-center gap-3 rounded-2xl border border-forest/15
                 bg-forest/[0.06] px-4 py-3 shadow-sm transition-colors
                 hover:bg-forest/[0.09]"
    >
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                   bg-forest text-white"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
          Saved to {surface}
        </p>
        <p className="mt-0.5 truncate text-sm font-medium text-text">
          {reveal.label}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onOpen(reveal)}
        className="inline-flex items-center gap-1 rounded-full bg-forest px-3 py-1.5
                   text-xs font-medium text-white transition-transform
                   hover:scale-[1.02] active:scale-[0.98]
                   focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-forest/40 focus-visible:ring-offset-1"
        aria-label={`Open ${reveal.label} in ${surface}`}
      >
        <span>Open in {surface}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}
